"use client";

import { ComponentPropsWithoutRef, KeyboardEvent, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type ChipVariant = "outlined" | "tonal" | "elevated";
export type ChipColor =
  | "default"
  | "primary"
  | "success"
  | "warning"
  | "error"
  | "info";
export type ChipSize = "sm" | "md" | "lg";

/** Override built-in color styles for custom one-off chips. */
export interface ChipStyles {
  base: string;
  selected: string;
}

export interface ChipProps extends Omit<
  ComponentPropsWithoutRef<"button">,
  "children"
> {
  label: string;
  variant?: ChipVariant;
  color?: ChipColor;
  size?: ChipSize;
  selected?: boolean;
  leadingIcon?: LucideIcon;
  trailingIcon?: LucideIcon;
  /** When provided, renders a dismiss (×) button and calls this on removal. */
  onDismiss?: () => void;
  /** Leading avatar element (e.g. an <img> or colored swatch). */
  avatar?: ReactNode;
  /**
   * Fully override the color/selected styles for this chip instance.
   * Useful for one-off colors without touching the component internals.
   *
   * @example
   * customStyles={{ base: "bg-pink-100 text-pink-700", selected: "bg-pink-200 text-pink-900" }}
   */
  customStyles?: ChipStyles;
}

// ─── Style tables ────────────────────────────────────────────────────────────

const colorStyles: Record<ChipVariant, Record<ChipColor, ChipStyles>> = {
  outlined: {
    default: {
      base: "border border-gray-400 text-gray-700 hover:bg-gray-400/[0.08]",
      selected: "bg-gray-200 text-gray-900",
    },
    primary: {
      base: "border border-navigator-primary/50 text-navigator-primary hover:bg-navigator-primary/[0.08]",
      selected: "bg-navigator-primary/15 text-navigator-primary-dark",
    },
    success: {
      base: "border border-green-400 text-green-700 hover:bg-green-400/[0.08]",
      selected: "bg-green-100 text-green-900",
    },
    warning: {
      base: "border border-amber-400 text-amber-700 hover:bg-amber-400/[0.08]",
      selected: "bg-amber-100 text-amber-900",
    },
    error: {
      base: "border border-red-400 text-red-600 hover:bg-red-400/[0.08]",
      selected: "bg-red-100 text-red-800",
    },
    info: {
      base: "border border-blue-400 text-blue-600 hover:bg-blue-400/[0.08]",
      selected: "bg-blue-100 text-blue-900",
    },
  },
  tonal: {
    default: {
      base: "bg-white text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-gray-400/[0.08]",
      selected: "bg-gray-200 text-gray-900",
    },
    primary: {
      base: "bg-navigator-primary/10 text-navigator-primary hover:bg-navigator-primary/[0.15]",
      selected: "bg-navigator-primary/20 text-navigator-primary-dark",
    },
    success: {
      base: "bg-green-100 text-green-700 hover:bg-green-400/[0.15]",
      selected: "bg-green-200 text-green-900",
    },
    warning: {
      base: "bg-amber-100 text-amber-700 hover:bg-amber-400/[0.15]",
      selected: "bg-amber-200 text-amber-900",
    },
    error: {
      base: "bg-red-100 text-red-600 hover:bg-red-400/[0.15]",
      selected: "bg-red-200 text-red-800",
    },
    info: {
      base: "bg-blue-100 text-blue-600 hover:bg-blue-400/[0.15]",
      selected: "bg-blue-200 text-blue-900",
    },
  },
  elevated: {
    default: {
      base: "bg-white text-gray-700 shadow-sm hover:shadow",
      selected: "bg-gray-100 text-gray-900 shadow-sm",
    },
    primary: {
      base: "bg-white text-navigator-primary shadow-sm hover:shadow",
      selected: "bg-navigator-primary/10 text-navigator-primary-dark shadow-sm",
    },
    success: {
      base: "bg-white text-green-700 shadow-sm hover:shadow",
      selected: "bg-green-100 text-green-900 shadow-sm",
    },
    warning: {
      base: "bg-white text-amber-700 shadow-sm hover:shadow",
      selected: "bg-amber-100 text-amber-900 shadow-sm",
    },
    error: {
      base: "bg-white text-red-600 shadow-sm hover:shadow",
      selected: "bg-red-100 text-red-800 shadow-sm",
    },
    info: {
      base: "bg-white text-blue-600 shadow-sm hover:shadow",
      selected: "bg-blue-100 text-blue-900 shadow-sm",
    },
  },
};

const sizeConfig = {
  sm: {
    chip: "h-6 px-2.5 text-xs gap-1",
    icon: "w-3 h-3",
    dismissPad: "p-0.5 -mr-0.5",
  },
  md: {
    chip: "h-8 px-3 text-sm gap-1.5",
    icon: "w-3.5 h-3.5",
    dismissPad: "p-0.5 -mr-0.5",
  },
  lg: {
    chip: "h-10 px-4 text-base gap-2",
    icon: "w-4 h-4",
    dismissPad: "p-1 -mr-1",
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export function Chip({
  label,
  variant = "tonal",
  color = "default",
  size = "md",
  selected = false,
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  onDismiss,
  avatar,
  customStyles,
  disabled,
  className,
  onClick,
  ...props
}: ChipProps) {
  const sz = sizeConfig[size];
  const styles = customStyles ?? colorStyles[variant][color];

  function handleDismissKeyDown(e: KeyboardEvent<HTMLSpanElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      onDismiss?.();
    }
  }

  function renderLeading() {
    if (avatar) {
      return (
        <span className="shrink-0 leading-none" aria-hidden>
          {avatar}
        </span>
      );
    }
    return (
      <span
        className={cn(
          "shrink-0 inline-flex items-center transition-all duration-200 ease-out",
          LeadingIcon ? cn("relative justify-center", sz.icon) : (selected ? sz.icon : "w-0 overflow-hidden"),
        )}
      >
        {LeadingIcon && (
          <LeadingIcon
            className={cn(sz.icon, "transition-all duration-200 ease-out", selected ? "opacity-0 scale-50" : "opacity-100 scale-100")}
            aria-hidden
          />
        )}
        <Check
          className={cn(sz.icon, "transition-all duration-200 ease-out", LeadingIcon && "absolute", selected ? "opacity-100 scale-100" : "opacity-0 scale-50")}
          aria-hidden
        />
      </span>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={onClick ? selected : undefined}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex items-center rounded-full font-medium transition-all duration-150 select-none cursor-pointer",
        "focus-visible:outline-2 focus-visible:outline-offset-2",
        "disabled:opacity-40 disabled:pointer-events-none",
        !onClick && !onDismiss && "pointer-events-none",
        sz.chip,
        selected ? styles.selected : styles.base,
        className,
      )}
      {...props}
    >
      {renderLeading()}

      <span className="leading-none">{label}</span>

      {TrailingIcon && !onDismiss && (
        <TrailingIcon className={cn(sz.icon, "shrink-0")} aria-hidden />
      )}

      {onDismiss && (
        <span
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={`Remove ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          onKeyDown={handleDismissKeyDown}
          className={cn(
            "rounded-full hover:bg-black/10 transition-colors shrink-0 cursor-pointer",
            sz.dismissPad,
          )}
        >
          <X className={sz.icon} aria-hidden />
        </span>
      )}
    </button>
  );
}
